import { isPublicOrderId } from '../../shared/publicOrderId.ts'
import type { CheckoutTermsSignal } from '../../shared/checkoutTerms'
import { businessData } from '../config/businessData'
import { buildCheckoutMeasurementEnvelope } from '../analytics/metaAttribution'
import type { CartAddOn } from '../order/addOnStorage'
import type { CartBox } from '../order/cartStorage'
import { getSelectedCheckoutPlace } from './addressAutocomplete'
import type { CheckoutDraftValues } from './checkoutStorage'
import { normalizeNzPhone } from './checkoutValidation'

export type DeliveryQuote = {
  eligible: boolean
  fee: number
  currency: 'NZD'
  quoteId: string
}

export type CheckoutAddOnPayload = {
  id: string
  name: string
  packLabel: string
  shortDescription: string
  unitPriceCents: number
  imageUrl: string | null
  quantity: number
}

export type CheckoutOrderPayload = {
  termsAcceptance?: CheckoutTermsSignal
  cartFingerprint: string
  boxes: CartBox[]
  addons?: CheckoutAddOnPayload[]
  contact: {
    fullName: string
    phone: string
    email: string
  }
  delivery: {
    address: string
    suburb: string
    postcode: string
    deliveryDay: string
    deliveryNotes: string
    quoteId: string
  }
  discountCode: string
}

export type DiscountPreview = {
  code: string
  offerLabel: string
  subtotalCents: number
  discountCents: number
  deliveryFeeCents: number
  totalCents: number
  currency: 'NZD'
}

export type HostedCheckoutSession = {
  checkoutUrl: string
  sessionId: string
  amountCents: number
  subtotalCents: number
  discountCents: number
  deliveryFeeCents: number
  discountCode: string | null
  currency: 'NZD'
}

export type HostedCheckoutResolution =
  | {
    confirmed: true
    redirectUrl: string
    orderId: string
    amountCents: number
    subtotalCents: number
    discountCents: number
    deliveryFeeCents: number
    paymentStatus: 'paid'
    purchaseSuppressed?: boolean
    measurementToken?: string
  }
  | {
    confirmed: false
    sessionId: string
    status: string
    paymentStatus: string
    retryAllowed?: boolean
  }

export type BankTransferDetails = {
  accountName: string
  accountNumber: string
  reference: string
  pendingMessage: string
}

export type ConfirmedOrder = {
  confirmed: boolean
  redirectUrl: string
  orderId?: string
  amountCents?: number
  subtotalCents?: number
  discountCents?: number
  deliveryFeeCents?: number
  bankTransfer?: BankTransferDetails
}

export const CHECKOUT_DELIVERY_ELIGIBILITY_EVENT = 'sg:checkout-delivery-eligibility'

export type CheckoutDeliveryEligibilityDetail = {
  state: 'eligible' | 'outside-area' | 'unverified' | 'unavailable'
  message: string | null
}

const OUTSIDE_DELIVERY_AREA_MESSAGE = 'Sorry, this address is outside our delivery area. We currently deliver up to 35 km from our Christchurch delivery base.'
const DELIVERY_UNAVAILABLE_MESSAGE = 'The delivery check is temporarily unavailable. Retry the delivery check to continue.'
const ADDRESS_UNVERIFIED_MESSAGE = 'We could not verify this address. Check the street address, suburb and postcode, then try again.'
const BANK_CONFIRMATION_STORAGE_PREFIX = 'sg.checkout.bank-transfer.v1:'

export class CheckoutApiError extends Error {
  readonly status: number | null
  readonly code: string | null

  constructor(message: string, status: number | null = null, code: string | null = null) {
    super(message)
    this.name = 'CheckoutApiError'
    this.status = status
    this.code = code
  }
}

function dispatchDeliveryEligibility(detail: CheckoutDeliveryEligibilityDetail) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<CheckoutDeliveryEligibilityDetail>(CHECKOUT_DELIVERY_ELIGIBILITY_EVENT, { detail }))
}

async function postJson(path: string, body: unknown, signal?: AbortSignal): Promise<unknown> {
  if (!businessData.checkout.api.enabled) throw new CheckoutApiError('Checkout API is not configured.')

  let response: Response
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
      signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new CheckoutApiError('Checkout service could not be reached.')
  }

  let payload: unknown = null
  try { payload = await response.json() } catch { /* status remains useful */ }
  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string'
      ? payload.message
      : 'Checkout service rejected the request.'
    const code = payload && typeof payload === 'object' && 'code' in payload && typeof payload.code === 'string' ? payload.code : null
    throw new CheckoutApiError(message, response.status, code)
  }
  return payload
}

async function getJson(path: string): Promise<unknown> {
  if (!businessData.checkout.api.enabled) throw new CheckoutApiError('Checkout API is not configured.')

  let response: Response
  try {
    response = await fetch(path, { method: 'GET', credentials: 'same-origin' })
  } catch {
    throw new CheckoutApiError('Checkout service could not be reached.')
  }

  let payload: unknown = null
  try { payload = await response.json() } catch { /* status remains useful */ }
  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string'
      ? payload.message
      : 'Checkout service rejected the request.'
    const code = payload && typeof payload === 'object' && 'code' in payload && typeof payload.code === 'string' ? payload.code : null
    throw new CheckoutApiError(message, response.status, code)
  }
  return payload
}

function isNzd(value: unknown): value is 'NZD' { return value === 'NZD' }


export async function requestDeliveryQuote(
  details: { address: string; suburb: string; postcode: string },
  signal?: AbortSignal,
): Promise<DeliveryQuote> {
  const selectedPlace = getSelectedCheckoutPlace(details.address)

  let payload: unknown
  try {
    payload = await postJson(businessData.checkout.api.deliveryQuotePath, {
      ...details,
      verifiedPlace: selectedPlace ? {
        provider: 'google', placeId: selectedPlace.placeId, formattedAddress: selectedPlace.formattedAddress,
        latitude: selectedPlace.latitude, longitude: selectedPlace.longitude,
      } : null,
    }, signal)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    const code = error instanceof CheckoutApiError && error.status === 422 ? error.code : null
    const state = code === 'delivery-ineligible' ? 'outside-area' : code === 'address-not-verified' ? 'unverified' : 'unavailable'
    const message = state === 'outside-area' ? OUTSIDE_DELIVERY_AREA_MESSAGE : state === 'unverified' ? ADDRESS_UNVERIFIED_MESSAGE : DELIVERY_UNAVAILABLE_MESSAGE
    dispatchDeliveryEligibility({ state, message })
    throw new CheckoutApiError(message, error instanceof CheckoutApiError ? error.status : null, code)
  }

  if (!payload || typeof payload !== 'object') {
    dispatchDeliveryEligibility({ state: 'unavailable', message: DELIVERY_UNAVAILABLE_MESSAGE })
    throw new CheckoutApiError('Delivery quote response was invalid.')
  }
  const record = payload as Record<string, unknown>
  if (typeof record.eligible !== 'boolean'
    || typeof record.fee !== 'number' || !Number.isFinite(record.fee) || record.fee < 0
    || !isNzd(record.currency)
    || typeof record.quoteId !== 'string' || !record.quoteId) {
    dispatchDeliveryEligibility({ state: 'unavailable', message: DELIVERY_UNAVAILABLE_MESSAGE })
    throw new CheckoutApiError('Delivery quote response was invalid.')
  }
  if (!record.eligible) dispatchDeliveryEligibility({ state: 'outside-area', message: OUTSIDE_DELIVERY_AREA_MESSAGE })
  else dispatchDeliveryEligibility({ state: 'eligible', message: null })
  return { eligible: record.eligible, fee: record.fee, currency: record.currency, quoteId: record.quoteId }
}

export async function requestDiscountPreview(order: CheckoutOrderPayload): Promise<DiscountPreview> {
  const payload = await postJson('/api/checkout/discount/validate', order)
  if (!payload || typeof payload !== 'object') throw new CheckoutApiError('Discount response was invalid.')
  const record = payload as Record<string, unknown>
  if (typeof record.code !== 'string' || !record.code
    || typeof record.offerLabel !== 'string' || !record.offerLabel
    || !Number.isInteger(record.subtotalCents)
    || !Number.isInteger(record.discountCents)
    || !Number.isInteger(record.deliveryFeeCents)
    || !Number.isInteger(record.totalCents)
    || !isNzd(record.currency)) throw new CheckoutApiError('Discount response was invalid.')
  return {
    code: record.code, offerLabel: record.offerLabel,
    subtotalCents: Number(record.subtotalCents), discountCents: Number(record.discountCents),
    deliveryFeeCents: Number(record.deliveryFeeCents), totalCents: Number(record.totalCents), currency: record.currency,
  }
}

export async function createHostedCheckoutSession(
  order: CheckoutOrderPayload,
  expectedTotalCents: number,
): Promise<HostedCheckoutSession> {
  const payload = await postJson(businessData.checkout.api.hostedCheckoutPath, {
    ...order,
    expectedTotalCents,
    measurement: buildCheckoutMeasurementEnvelope(),
  })
  if (!payload || typeof payload !== 'object') throw new CheckoutApiError('Payment response was invalid.')
  const record = payload as Record<string, unknown>
  if (typeof record.checkoutUrl !== 'string' || !record.checkoutUrl.startsWith('https://')
    || typeof record.sessionId !== 'string' || !record.sessionId.startsWith('cs_')
    || !Number.isInteger(record.amountCents) || Number(record.amountCents) < 0
    || !Number.isInteger(record.subtotalCents)
    || !Number.isInteger(record.discountCents)
    || !Number.isInteger(record.deliveryFeeCents)
    || (record.discountCode !== null && typeof record.discountCode !== 'string')
    || !isNzd(record.currency)) throw new CheckoutApiError('Payment response was invalid.')
  return {
    checkoutUrl: record.checkoutUrl,
    sessionId: record.sessionId,
    amountCents: Number(record.amountCents),
    subtotalCents: Number(record.subtotalCents),
    discountCents: Number(record.discountCents),
    deliveryFeeCents: Number(record.deliveryFeeCents),
    discountCode: record.discountCode as string | null,
    currency: record.currency,
  }
}

export async function resolveHostedCheckoutReturn(sessionId: string, cancel?: boolean): Promise<HostedCheckoutResolution> {
  const cleanSessionId = sessionId.trim()
  if (!cleanSessionId.startsWith('cs_')) throw new CheckoutApiError('Checkout Session is invalid.')
  const payload = cancel === undefined
    ? await getJson(`/api/checkout/checkout-session/status?session_id=${encodeURIComponent(cleanSessionId)}`)
    : await postJson('/api/checkout/checkout-session/status', { sessionId: cleanSessionId, cancel })
  if (!payload || typeof payload !== 'object') throw new CheckoutApiError('Payment status response was invalid.')
  const record = payload as Record<string, unknown>

  if (record.confirmed === true) {
    if (typeof record.redirectUrl !== 'string' || !record.redirectUrl.startsWith('/')
      || typeof record.orderId !== 'string' || !isPublicOrderId(record.orderId)
      || !Number.isInteger(record.amountCents)
      || !Number.isInteger(record.subtotalCents)
      || !Number.isInteger(record.discountCents)
      || !Number.isInteger(record.deliveryFeeCents)
      || record.paymentStatus !== 'paid') {
      throw new CheckoutApiError('Payment confirmation response was invalid.')
    }
    return {
      confirmed: true,
      redirectUrl: record.redirectUrl,
      orderId: record.orderId,
      amountCents: Number(record.amountCents),
      subtotalCents: Number(record.subtotalCents),
      discountCents: Number(record.discountCents),
      deliveryFeeCents: Number(record.deliveryFeeCents),
      paymentStatus: 'paid',
      purchaseSuppressed: record.purchaseSuppressed === true,
      measurementToken: typeof record.measurementToken === 'string' ? record.measurementToken : undefined,
    }
  }

  if (record.confirmed === false
    && typeof record.sessionId === 'string'
    && typeof record.status === 'string'
    && typeof record.paymentStatus === 'string') {
    return {
      confirmed: false,
      sessionId: record.sessionId,
      status: record.status,
      paymentStatus: record.paymentStatus,
      retryAllowed: record.retryAllowed === true && record.sessionId === cleanSessionId
        && record.status === 'expired' && record.paymentStatus === 'unpaid',
    }
  }

  throw new CheckoutApiError('Payment status response was invalid.')
}

export type BrowserPurchaseDiagnosticState = 'queued' | 'not-permitted' | 'not-configured'

export async function recordBrowserPurchaseDiagnostic(
  orderId: string,
  measurementToken: string,
  state: BrowserPurchaseDiagnosticState,
): Promise<void> {
  if (!isPublicOrderId(orderId) || !/^(?:cs_|pi_)[A-Za-z0-9_]+$/.test(measurementToken)) return
  try {
    await postJson('/api/measurement/browser-purchase', { orderId, measurementToken, state })
  } catch {
    // Diagnostics are best effort and must never affect the confirmation page.
  }
}

export async function placeBankTransferOrder(order: CheckoutOrderPayload, expectedTotalCents: number): Promise<ConfirmedOrder> {
  const confirmed = parseConfirmedOrder(await postJson(businessData.checkout.api.bankTransferOrderPath, { ...order, expectedTotalCents }))
  if (confirmed.confirmed && confirmed.orderId && confirmed.bankTransfer && typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(`${BANK_CONFIRMATION_STORAGE_PREFIX}${confirmed.orderId}`, JSON.stringify({
        version: 1,
        accountName: confirmed.bankTransfer.accountName,
        accountNumber: confirmed.bankTransfer.accountNumber,
      }))
    } catch {
      // Confirmation email retains the authoritative order snapshot if browser storage is unavailable.
    }
  }
  return confirmed
}

function parseConfirmedOrder(payload: unknown): ConfirmedOrder {
  if (!payload || typeof payload !== 'object') throw new CheckoutApiError('Order confirmation response was invalid.')
  const record = payload as Record<string, unknown>
  if (typeof record.confirmed !== 'boolean' || typeof record.redirectUrl !== 'string' || !record.redirectUrl) {
    throw new CheckoutApiError('Order confirmation response was invalid.')
  }
  if (record.confirmed && !isPublicOrderId(record.orderId)) {
    throw new CheckoutApiError('Order confirmation response was invalid.')
  }
  let bankTransfer: BankTransferDetails | undefined
  if (record.bankTransfer !== undefined) {
    if (!record.bankTransfer || typeof record.bankTransfer !== 'object' || Array.isArray(record.bankTransfer)) {
      throw new CheckoutApiError('Bank transfer response was invalid.')
    }
    const bank = record.bankTransfer as Record<string, unknown>
    if (typeof bank.accountName !== 'string' || !bank.accountName
      || typeof bank.accountNumber !== 'string' || !/^\d{2}-\d{4}-\d{7}-\d{3}$/.test(bank.accountNumber)
      || typeof bank.reference !== 'string' || !isPublicOrderId(bank.reference)
      || typeof bank.pendingMessage !== 'string' || !bank.pendingMessage) {
      throw new CheckoutApiError('Bank transfer response was invalid.')
    }
    bankTransfer = {
      accountName: bank.accountName,
      accountNumber: bank.accountNumber,
      reference: bank.reference,
      pendingMessage: bank.pendingMessage,
    }
  }
  return {
    confirmed: record.confirmed,
    redirectUrl: record.redirectUrl,
    orderId: typeof record.orderId === 'string' ? record.orderId : undefined,
    amountCents: Number.isInteger(record.amountCents) ? Number(record.amountCents) : undefined,
    subtotalCents: Number.isInteger(record.subtotalCents) ? Number(record.subtotalCents) : undefined,
    discountCents: Number.isInteger(record.discountCents) ? Number(record.discountCents) : undefined,
    deliveryFeeCents: Number.isInteger(record.deliveryFeeCents) ? Number(record.deliveryFeeCents) : undefined,
    bankTransfer,
  }
}

export function buildCheckoutOrderPayload(
  boxes: CartBox[],
  draft: CheckoutDraftValues,
  quoteId: string,
  addOns: readonly CartAddOn[] = [],
): CheckoutOrderPayload {
  const boxFingerprint = boxes.map((box) => [
    box.id, box.quantity, box.price,
    box.selections.map((selection) => `${selection.productId}:${selection.quantity}`).join(','),
  ].join(':')).join('|')

  const addons: CheckoutAddOnPayload[] = addOns.map((item) => ({
    id: item.id,
    name: item.name,
    packLabel: item.packLabel,
    shortDescription: item.shortDescription,
    unitPriceCents: item.unitPriceCents,
    imageUrl: item.imageUrl,
    quantity: item.quantity,
  }))
  const addonFingerprint = addons.length
    ? [...addons]
      .sort((left, right) => left.id.localeCompare(right.id))
      .map((item) => `${item.id}:${item.quantity}:${item.unitPriceCents}`)
      .join(',')
    : ''
  const cartFingerprint = addonFingerprint
    ? `${boxFingerprint}||addons|${addonFingerprint}`
    : boxFingerprint

  return {
    cartFingerprint,
    boxes,
    ...(addons.length ? { addons } : {}),
    contact: {
      fullName: draft.fullName.trim(), phone: normalizeNzPhone(draft.phone), email: draft.email.trim(),
    },
    delivery: {
      address: draft.address.trim(), suburb: draft.suburb.trim(), postcode: draft.postcode.trim(),
      deliveryDay: draft.deliveryDay, deliveryNotes: draft.deliveryNotes.trim(), quoteId,
    },
    discountCode: draft.discountCode.trim().toUpperCase(),
  }
}

// Volley capabilities stay session-scoped. They never attest payment status.
const VOLLEY_CAPABILITY = 'sg.checkout.volley.intent.v1'
export type VolleyStatus = {
  attemptId: string; state: string; confirmed: boolean; orderId: string | null
  amountCents: number; currency: 'NZD'; checkoutUrl: string | null
  testMode?: boolean
  testEvidence?: 'successful' | 'awaiting' | null
  snapshot?: CheckoutOrderPayload
  receipt?: { subtotalCents: number; discountCents: number; deliveryFeeCents: number }
}
export function volleyCapability(create = false): string | null {
  try {
    let value = window.sessionStorage.getItem(VOLLEY_CAPABILITY)
    if (!value && create) {
      value = [...crypto.getRandomValues(new Uint8Array(32))].map(b => b.toString(16).padStart(2, '0')).join('')
      window.sessionStorage.setItem(VOLLEY_CAPABILITY, value)
    }
    return value ?? (!create ? window.sessionStorage.getItem(`${VOLLEY_CAPABILITY}.receipt`) : null)
  } catch { return null }
}
export async function fetchVolleyMode(): Promise<'legacy' | 'volley' | 'disabled'> {
  const value = await getJson('/api/checkout/volley/availability') as Record<string,unknown>
  if (!['legacy','volley','disabled'].includes(String(value.mode))) throw new CheckoutApiError('Payment availability could not be verified.')
  return value.mode as 'legacy' | 'volley' | 'disabled'
}
function parseVolleyStatus(value: unknown): VolleyStatus {
  if (!value || typeof value !== 'object') throw new CheckoutApiError('Payment response was invalid.')
  const r = value as Record<string,unknown>
  const receipt = r.receipt as VolleyStatus['receipt']
  if ((r.testEvidence !== undefined && r.testEvidence !== null && (typeof r.testEvidence !== 'string' || !['successful','awaiting'].includes(r.testEvidence) || r.testMode !== true))
    || (r.testMode !== undefined && typeof r.testMode !== 'boolean')
    || (r.testMode === true && (r.confirmed !== false || r.orderId !== null || r.snapshot !== undefined || receipt !== undefined))) {
    throw new CheckoutApiError('Payment response was invalid.')
  }
  if (typeof r.attemptId !== 'string' || typeof r.state !== 'string' || typeof r.confirmed !== 'boolean'
    || !Number.isInteger(r.amountCents) || Number(r.amountCents) <= 0 || r.currency !== 'NZD'
    || (r.confirmed && (r.state !== 'complete' || !isPublicOrderId(r.orderId) || !r.snapshot || !receipt
      || ![receipt.subtotalCents,receipt.discountCents,receipt.deliveryFeeCents].every(n => Number.isInteger(n) && n >= 0)
      || receipt.subtotalCents - receipt.discountCents + receipt.deliveryFeeCents !== r.amountCents))) throw new CheckoutApiError('Payment response was invalid.')
  if (r.checkoutUrl !== null) {
    const url = new URL(String(r.checkoutUrl))
    if (url.protocol !== 'https:' || url.hostname !== 'app.volley.nz' || url.port || url.username || url.password || !url.pathname.startsWith('/pay/')) throw new CheckoutApiError('Payment redirect was invalid.')
  }
  return r as VolleyStatus
}
export async function beginVolley(order: CheckoutOrderPayload, expectedTotalCents: number) {
  const capability = volleyCapability(true)
  if (!capability) throw new CheckoutApiError('Browser session storage is needed to recover Pay via Volley. Please use card checkout or contact us.')
  return parseVolleyStatus(await postJson('/api/checkout/volley', {...order, expectedTotalCents, capability}))
}
export async function fetchVolleyStatus(paymentId?: string) {
  const capability = volleyCapability()
  if (!capability) throw new CheckoutApiError('This payment session is unavailable. Contact Sugar Gremlin before paying again.')
  return parseVolleyStatus(await postJson('/api/checkout/volley/status', {capability, ...(paymentId ? {paymentId} : {})}))
}

export function finishVolleySession() {
  try {
    const cap = window.sessionStorage.getItem(VOLLEY_CAPABILITY)
    if (cap) window.sessionStorage.setItem(`${VOLLEY_CAPABILITY}.receipt`,cap)
    window.sessionStorage.removeItem(VOLLEY_CAPABILITY)
  } catch { /* server-owned receipt and email remain authoritative */ }
}
